import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import { Add as AddIcon, Person as PersonIcon, Edit as EditIcon } from '@mui/icons-material';
import { useAuth, UserLevel } from '../../../../contexts/AuthContext';
import MainLayout from '../../../../components/Layout/MainLayout';
import httpClient from '../../../../utils/httpClient';
import { ApiErrorResponse, ApiSuccessResponse } from '../../../../utils/apiSchemas';
import ApiConnectionAlert from '../../../../components/ApiConnectionAlert/ApiConnectionAlert';

interface User {
  id: string;
  name: string;
  email: string;
  companyName: string;
  status: string;
  userLevel: UserLevel;
  createdAt: Date;
}

// Companies returned from API
interface CompanyItem {
  guid: string;
  fantasyName: string;
}

// API response item shape for list-all
interface UserAccessApiItem {
  name: string;
  email: string;
  companyFantasyName: string;
  userLevel: number;
  userLevelDescription: string;
  statusAccount: number;
  createdDate: string;
  guid?: string;
}

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [formData, setFormData] = useState<{ name: string; email: string; companyGuid: string; userLevel: UserLevel }>({
    name: '',
    email: '',
    companyGuid: 'NONE',
    userLevel: UserLevel.NONE
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isAlertSuccess, setIsAlertSuccess] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState<{ name: string; email: string; companyGuid: string; userLevel: UserLevel }>({
    name: '',
    email: '',
    companyGuid: '',
    userLevel: UserLevel.NONE
  });

  const mapUserLevel = (lvl: number): UserLevel => {
    switch (lvl) {
      case 1: return UserLevel.SUPER_ADMIN_USER;
      case 2: return UserLevel.ADMIN_USER;
      case 3: return UserLevel.GENERAL_USER;
      default: return UserLevel.GENERAL_USER;
    }
  };

  const mapStatusAccountToLabel = (code: number): string => {
    switch (code) {
      case 1: return t('user_mgmt_status_active');
      case 0: return t('user_mgmt_status_pending_confirmation');
      default: return t('user_mgmt_status_inactive');
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await httpClient.get('/api/v1/portal/user-access/list-all', {
          params: { pageNumber, pageSize }
        });

        const raw = res.data as ApiSuccessResponse<{ companies: CompanyItem[]; userAccess: UserAccessApiItem[] }> & Partial<ApiErrorResponse>;

        if (raw && raw.success && raw.data && Array.isArray(raw.data.userAccess)) {
          const mapped: User[] = raw.data.userAccess.map((item) => ({
            id: (item as any).guid || item.guid || item.email || `${item.name}-${item.createdDate}`,
            name: item.name,
            email: item.email,
            companyName: item.companyFantasyName,
            status: mapStatusAccountToLabel(item.statusAccount),
            userLevel: mapUserLevel(item.userLevel),
            createdAt: new Date(item.createdDate)
          }));
          setUsers(mapped);
          if (Array.isArray(raw.data.companies)) {
            setCompanies(raw.data.companies);
          }
        } else {
          const errMsg = (raw as ApiErrorResponse)?.errors?.[0]?.errorMessage || t('user_mgmt_error_load_users');
          setAlertMessage(errMsg);
          setIsAlertSuccess(false);
          setShowAlert(true);
        }
      } catch (error: any) {
        setAlertMessage(t('user_mgmt_error_fetch_users'));
        setIsAlertSuccess(false);
        setShowAlert(true);
      }
    };

    fetchUsers();
  }, [pageNumber, pageSize]);

  const handleInputChange = (field: string, value: string | number | UserLevel) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getCompanyNameByGuid = (guid: string): string => {
    const company = companies.find(c => c.guid === guid);
    return company ? company.fantasyName : '';
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.companyGuid || formData.companyGuid === 'NONE') {
      setAlertMessage(t('common_error_required_fields'));
      setIsAlertSuccess(false);
      setShowAlert(true);
      return;
    }

    // Verificar se email já existe
    const emailExists = users.some(user => user.email === formData.email);
    if (emailExists) {
      setAlertMessage(t('user_mgmt_error_email_exists'));
      setIsAlertSuccess(false);
      setShowAlert(true);
      return;
    }

    // Exigir seleção de Tipo de Usuário conforme nível do usuário logado
    if ((isSuperUser || isAdminUser) && formData.userLevel === UserLevel.NONE) {
      setAlertMessage(t('user_mgmt_error_select_user_level'));
      setIsAlertSuccess(false);
      setShowAlert(true);
      return;
    }
    try {
      // Montar payload conforme especificação
      const payload = {
        userName: formData.name,
        emailAddress: formData.email,
        responsableName: formData.name,
        userLevel: Number(formData.userLevel === UserLevel.NONE ? UserLevel.GENERAL_USER : formData.userLevel)
      };

      const res = await httpClient.post('/api/v1/portal/enrollment/add-user-access-to-customer', payload);

      // Tratar possíveis erros na resposta OK
      const maybeErrors = (res?.data && (res.data as any).errors) || [];
      if (Array.isArray(maybeErrors) && maybeErrors.length > 0) {
        const msg = maybeErrors.map((e: any) => `${e.propertyName || ''}: ${e.errorMessage || ''}`.trim()).join(' | ');
        setAlertMessage(msg || t('user_mgmt_error_update_user_try_again'));
        setIsAlertSuccess(false);
        setShowAlert(true);
        return;
      }

      // Recarregar lista da API para refletir o novo usuário com dados oficiais
      try {
        const listRes = await httpClient.get('/api/v1/portal/user-access/list-all', { params: { pageNumber, pageSize } });
        const raw = listRes.data as ApiSuccessResponse<{ companies: CompanyItem[]; userAccess: UserAccessApiItem[] }> & Partial<ApiErrorResponse>;
        if (raw && raw.success && raw.data && Array.isArray(raw.data.userAccess)) {
          const mapped: User[] = raw.data.userAccess.map((item) => ({
            id: (item as any).guid || item.guid || item.email || `${item.name}-${item.createdDate}`,
            name: item.name,
            email: item.email,
            companyName: item.companyFantasyName,
            status: mapStatusAccountToLabel(item.statusAccount),
            userLevel: mapUserLevel(item.userLevel),
            createdAt: new Date(item.createdDate)
          }));
          setUsers(mapped);
          if (Array.isArray(raw.data.companies)) {
            setCompanies(raw.data.companies);
          }
        }
      } catch (_) {
        // Se falhar o refresh, seguir com sucesso e manter estado atual
      }

      // Limpar formulário
      setFormData({
        name: '',
        email: '',
        companyGuid: 'NONE',
        userLevel: UserLevel.NONE
      });

      setAlertMessage(t('user_mgmt_success_user_created'));
      setIsAlertSuccess(true);
      setShowAlert(true);
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        const msg = apiErrors.map((e: any) => `${e.propertyName || ''}: ${e.errorMessage || ''}`.trim()).join(' | ');
        setAlertMessage(msg || t('user_mgmt_error_update_user_try_again'));
      } else {
        setAlertMessage(t('user_mgmt_error_update_user_try_again'));
      }
      setIsAlertSuccess(false);
      setShowAlert(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case t('user_mgmt_status_active'):
        return 'success';
      case t('user_mgmt_status_pending_confirmation'):
        return 'warning';
      case t('user_mgmt_status_inactive'):
        return 'error';
      default:
        return 'default';
    }
  };

  const getUserLevelText = (level: UserLevel) => {
    switch (level) {
      case UserLevel.SUPER_ADMIN_USER:
        return t('user_mgmt_level_super');
      case UserLevel.ADMIN_USER:
        return t('user_mgmt_level_admin');
      case UserLevel.GENERAL_USER:
      default:
        return t('user_mgmt_level_general');
    }
  };

  // Regras de exibição conforme o nível do usuário logado
  const { isSuperUser, isAdminUser } = useAuth();

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      companyGuid: (companies.find(c => c.fantasyName === user.companyName)?.guid) || '',
      userLevel: user.userLevel
    });
    setEditModalOpen(true);
  };

  const handleEditInputChange = (field: string, value: string | UserLevel) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSubmit = async () => {
    if (!selectedUser) return;

    // Validação de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.email)) {
      setAlertMessage(t('common_error_invalid_email'));
      setShowAlert(true);
      return;
    }

    // Verificar se email já existe (exceto o próprio usuário)
    const emailExists = users.some(user => 
      user.email === editFormData.email && user.id !== selectedUser.id
    );
    if (emailExists) {
      setAlertMessage(t('user_mgmt_error_email_exists'));
      setIsAlertSuccess(false);
      setShowAlert(true);
      return;
    }

    // Exigir seleção de Tipo de Usuário quando permitido alterar
    if ((isSuperUser || isAdminUser) && editFormData.userLevel === UserLevel.NONE) {
      setAlertMessage(t('user_mgmt_error_select_user_level'));
      setIsAlertSuccess(false);
      setShowAlert(true);
      return;
    }

    try {
      const payload = {
        userAccessGuid: selectedUser.id,
        name: editFormData.name,
        userLevelType: Number(editFormData.userLevel)
      };

      const res = await httpClient.put('/api/v1/portal/user-access/update-basic-info', payload);

      // Trata respostas com errors dentro de 200 OK
      const maybeErrors = (res?.data && (res.data as any).errors) || [];
      if (Array.isArray(maybeErrors) && maybeErrors.length > 0) {
        const msg = maybeErrors.map((e: any) => `${e.propertyName || ''}: ${e.errorMessage || ''}`.trim()).join(' | ');
        setAlertMessage(msg || t('user_mgmt_error_update_user'));
        setIsAlertSuccess(false);
        setShowAlert(true);
        return;
      }

      // Atualiza estado local com os novos dados
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id 
          ? {
              ...user,
              name: editFormData.name,
              email: editFormData.email,
              companyName: getCompanyNameByGuid(editFormData.companyGuid),
              userLevel: editFormData.userLevel as UserLevel
            }
          : user
      ));

      setEditModalOpen(false);
      setSelectedUser(null);
      setAlertMessage(t('user_mgmt_success_user_updated'));
      setIsAlertSuccess(true);
      setShowAlert(true);
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        const msg = apiErrors.map((e: any) => `${e.propertyName || ''}: ${e.errorMessage || ''}`.trim()).join(' | ');
        setAlertMessage(msg || t('user_mgmt_error_update_user'));
      } else {
        setAlertMessage(t('user_mgmt_error_update_user_try_again'));
      }
      setIsAlertSuccess(false);
      setShowAlert(true);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          {t('user_mgmt_title')}
        </Typography>
        <ApiConnectionAlert />

        {/* Formulário de Criação */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('user_mgmt_create_title')}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid component={'div' as React.ElementType} item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('user_mgmt_label_name')}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                variant="outlined"
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
            
            <Grid component={'div' as React.ElementType} item xs={12} md={4}>
              <TextField
                fullWidth
                label={t('user_mgmt_label_email')}
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                variant="outlined"
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
            
            <Grid component={'div' as React.ElementType} item xs={12} md={4}>
              <FormControl fullWidth sx={{ minWidth: 250 }}>
                <InputLabel id="createCompanyLabel" shrink>{t('user_mgmt_label_company')}</InputLabel>
                <Select
                  id="createCompany"
                  labelId="createCompanyLabel"
                  value={formData.companyGuid || ''}
                  label={t('user_mgmt_label_company')}
                  onChange={(e) => handleInputChange('companyGuid', e.target.value)}
                  displayEmpty
                  notched
                  MenuProps={{ disablePortal: true }}
                  renderValue={(selected) => {
                    const val = String(selected ?? '');
                    const opt = companies.find((c) => c.guid === val);
                    const isPlaceholder = !val;
                    const label = opt ? opt.fantasyName : t('user_mgmt_placeholder_select_company');
                    return (
                      <span style={{ fontStyle: isPlaceholder ? 'italic' : 'normal', color: isPlaceholder ? 'rgba(0,0,0,0.6)' : undefined }}>
                        {label}
                      </span>
                    );
                  }}
                >
                  <MenuItem value="NONE">
                    <em>{t('user_mgmt_placeholder_select_company')}</em>
                  </MenuItem>
                  {companies.length === 0 ? (
                    <MenuItem value="" disabled>
                      {t('user_mgmt_no_companies')}
                    </MenuItem>
                  ) : (
                    companies.map((company) => (
                      <MenuItem key={company.guid} value={company.guid}>
                        {company.fantasyName}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Seleção de Tipo de Usuário (UserLevel) */}
            {(isSuperUser || isAdminUser) && (
              <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                <FormControl fullWidth sx={{ minWidth: 250 }}>
                  <InputLabel id="createUserLevelLabel">{t('user_mgmt_label_user_level')}</InputLabel>
                  <Select
                    id="createUserLevel"
                    labelId="createUserLevelLabel"
                    value={formData.userLevel}
                    label={t('user_mgmt_label_user_level')}
                    onChange={(e) => handleInputChange('userLevel', Number(e.target.value) as UserLevel | 0)}
                    displayEmpty={true}
                    MenuProps={{ disablePortal: true }}
                  >
                    <MenuItem value={UserLevel.NONE}><em>{t('user_mgmt_placeholder_select_user_level')}</em></MenuItem>
                    {isSuperUser && (
                      <MenuItem value={UserLevel.SUPER_ADMIN_USER}>{t('user_mgmt_level_super')}</MenuItem>
                    )}
                    {(isSuperUser || isAdminUser) && (
                      <MenuItem value={UserLevel.ADMIN_USER}>{t('user_mgmt_level_admin')}</MenuItem>
                    )}
                    <MenuItem value={UserLevel.GENERAL_USER}>{t('user_mgmt_level_general')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleSubmit}
              sx={{ mr: 2 }}
              disabled={false}
            >
              {t('user_mgmt_btn_create_user')}
            </Button>
          </Box>
        </Paper>

        {/* Lista de Usuários */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('user_mgmt_list_title')}
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('user_mgmt_table_name')}</TableCell>
                  <TableCell>{t('user_mgmt_table_email')}</TableCell>
                  <TableCell>{t('user_mgmt_table_company')}</TableCell>
                  <TableCell>{t('user_mgmt_table_type')}</TableCell>
                  <TableCell>{t('user_mgmt_table_status')}</TableCell>
                  <TableCell>{t('user_mgmt_table_created_at')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => handleEditUser(user)}
                        sx={{ 
                          fontWeight: 'bold',
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText'
                          }
                        }}
                      >
                        {user.name}
                      </Button>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.companyName}</TableCell>
                    <TableCell>{getUserLevelText(user.userLevel)}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={getStatusColor(user.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.createdAt.toLocaleDateString('pt-BR')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Alert/Snackbar */}
        <Snackbar
          open={showAlert}
          autoHideDuration={6000}
          onClose={() => setShowAlert(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setShowAlert(false)}
            severity={isAlertSuccess ? 'success' : 'error'}
            sx={{ width: '100%' }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>

        {/* Edit User Modal */}
        <Dialog open={editModalOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon />
              {t('user_mgmt_dialog_edit_title')}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <TextField
                  fullWidth
                  label={t('user_mgmt_label_name')}
                  value={editFormData.name}
                  onChange={(e) => handleEditInputChange('name', e.target.value)}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>
              
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <TextField
                  fullWidth
                  label={t('user_mgmt_label_email')}
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => handleEditInputChange('email', e.target.value)}
                  inputProps={{ maxLength: 80 }}
                  disabled
                />
              </Grid>
              
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <FormControl fullWidth sx={{ minWidth: 250 }}>
                  <InputLabel id="editCompanyLabel">{t('user_mgmt_label_company')}</InputLabel>
                  <Select
                    id="editCompany"
                    labelId="editCompanyLabel"
                    value={editFormData.companyGuid}
                    label={t('user_mgmt_label_company')}
                    onChange={(e) => handleEditInputChange('companyGuid', e.target.value)}
                    displayEmpty={true}
                    MenuProps={{ disablePortal: true }}
                    disabled
                  >
                    <MenuItem value="">
                      <em>{t('user_mgmt_placeholder_select_company')}</em>
                    </MenuItem>
                    {companies.length === 0 ? (
                      <MenuItem value="" disabled>
                        {t('user_mgmt_no_companies')}
                      </MenuItem>
                    ) : (
                      companies.map((company) => (
                        <MenuItem key={company.guid} value={company.guid}>
                          {company.fantasyName}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <FormControl fullWidth sx={{ minWidth: 250 }}>
                  <InputLabel id="editUserLevelLabel">{t('user_mgmt_label_user_level')}</InputLabel>
                  <Select
                    id="editUserLevel"
                    labelId="editUserLevelLabel"
                    value={editFormData.userLevel}
                    label={t('user_mgmt_label_user_level')}
                    onChange={(e) => handleEditInputChange('userLevel', Number(e.target.value) as UserLevel | 0)}
                    displayEmpty={isSuperUser || isAdminUser}
                    MenuProps={{ disablePortal: true }}
                  >
                     <MenuItem value={UserLevel.NONE}><em>{t('user_mgmt_placeholder_select_user_level')}</em></MenuItem>
                     {isSuperUser && (
                      <MenuItem value={UserLevel.SUPER_ADMIN_USER}>{t('user_mgmt_level_super')}</MenuItem>
                     )}
                     {(isSuperUser || isAdminUser) && (
                      <MenuItem value={UserLevel.ADMIN_USER}>{t('user_mgmt_level_admin')}</MenuItem>
                     )}
                      <MenuItem value={UserLevel.GENERAL_USER}>{t('user_mgmt_level_general')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditModal}>
              {t('user_mgmt_btn_cancel')}
            </Button>
            <Button 
              onClick={handleEditSubmit} 
              variant="contained"
              disabled={!editFormData.name || !editFormData.email || !editFormData.companyGuid || ((isSuperUser || isAdminUser) && editFormData.userLevel === UserLevel.NONE)}
            >
              {t('user_mgmt_btn_save_changes')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default UserManagement;
